import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Home, Clock, Bookmark, BookmarkCheck, X, AlertCircle, Edit2 } from 'lucide-react';
import AnswerCard from './components/AnswerCard';
import QuestionRenderer from './components/QuestionRenderer';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import type { Question, Paper, FavoriteQuestion } from './types';
import { typeNames, subTypeNames } from '../../constants/questionTypes';
import { categoryStore } from '../../store/categoryStore';
import { getImageUrl } from '../../utils/questionHtml';
import { getTitleImageGridCols } from '../../utils/questionMapper';
import QuestionHtmlWithZoom from '../../components/QuestionHtmlWithZoom';
import PreviewControls from './components/PreviewControls';
import { useAutoSave } from '../../services/AutoSaveService';
import type { AutoSaveData } from '../../services/AutoSaveService';
import AutoSaveStatusIndicator, { AutoSaveStatusIndicatorInline } from '../../components/AutoSaveStatusIndicator';

const EXAM_DURATION = 60 * 60;

interface QuizAnswerModeProps {
  paper: Paper;
  examQuestions: Question[];
  currentUser: any;
  paperId?: string;
  isPreview?: boolean;
  isEditorPreview?: boolean;
  onBack: () => void;
}

export default function QuizAnswerMode({
  paper,
  examQuestions: initialQuestions,
  currentUser,
  paperId,
  isPreview = false,
  isEditorPreview = false,
  onBack
}: QuizAnswerModeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const examKey = `exam_${paperId || 'custom'}`;

  const [examQuestions, setExamQuestions] = useState<Question[]>(initialQuestions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [markedQuestions, setMarkedQuestions] = useState<Set<number>>(new Set());
  const [showSidebar, setShowSidebar] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteQuestion[]>([]);
  const [aiExplanations, setAiExplanations] = useState<Record<string, string>>({});
  const [aiGradingEnabled, setAiGradingEnabled] = useState(false);
  const [answerMode, setAnswerMode] = useState<'upload' | 'online'>('online');
  const [isAssignmentMode, setIsAssignmentMode] = useState(false);
  const [hasWidthChanges, setHasWidthChanges] = useState(false);
  const [isSavingWidths, setIsSavingWidths] = useState(false);
  const [initialWidths, setInitialWidths] = useState<Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidths?: number[] }[] }>>({});
  const [restoredFromCache, setRestoredFromCache] = useState(false);

  const currentQuestion = examQuestions[currentIndex];
  const answeredCount = Object.keys(answers).filter(key => !key.includes('_')).length;
  const isTimeWarning = timeLeft < 600;

  // ============ 自动保存服务 ============

  // 数据采集函数：收集当前答题状态
  const collectAutoSaveData = useCallback((): AutoSaveData => {
    return {
      answers,
      currentIndex,
      timeLeft,
      markedQuestions: Array.from(markedQuestions),
      isSubmitted,
      score,
      aiExplanations,
      savedAt: new Date().toISOString(),
    };
  }, [answers, currentIndex, timeLeft, markedQuestions, isSubmitted, score, aiExplanations]);

  // 保存函数：将数据提交到 exam_progress 表
  const saveToServer = useCallback(async (data: AutoSaveData): Promise<boolean> => {
    if (!currentUser?.id || isPreview) return false;
    try {
      const progress = {
        user_id: currentUser.id,
        exam_id: examKey,
        answers: data.answers,
        current_index: data.currentIndex,
        time_left: data.timeLeft,
        marked_questions: data.markedQuestions,
        is_submitted: data.isSubmitted,
        score: data.score,
        ai_explanations: data.aiExplanations,
        saved_at: data.savedAt,
      };
      const { data: existing } = await supabase.from('exam_progress').select('id').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
      if (existing) {
        const { error } = await supabase.from('exam_progress').update(progress).eq('id', existing.id);
        if (error) return false;
      } else {
        const { error } = await supabase.from('exam_progress').insert(progress);
        if (error) return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [currentUser?.id, examKey, isPreview]);

  // 使用自动保存 Hook
  const { statusInfo, triggerDebouncedSave, saveNow, clearLocalCache, resetAutoSave } = useAutoSave(
    examKey,
    collectAutoSaveData,
    saveToServer
  );

  // ============ 加载收藏和考试进度 ============

  useEffect(() => {
    if (!currentUser?.id || isPreview) return;

    const loadData = async () => {
      // 加载收藏
      const { data: favData } = await supabase.from('user_favorites').select('*').eq('user_id', currentUser.id);
      if (favData) {
        const mappedFavorites: FavoriteQuestion[] = favData.map((f: any) => ({
          id: f.id,
          questionId: f.question_id,
          content: '',
          category: f.category_code || '',
          level_text: f.level_number ? String(f.level_number) : '',
          correctAnswer: '',
          explanation: '',
          addTime: f.created_at,
          paperId: f.paper_id,
          paperTitle: ''
        }));
        setFavorites(mappedFavorites);
      }

      // 先检查是否已有提交记录，如有则不恢复任何进度
      const { count: submittedCount } = await supabase.from('exam_records').select('*', { count: 'exact' }).eq('user_id', currentUser.id).eq('paper_id', paperId || 'custom');
      const hasSubmitted = (submittedCount || 0) > 0;

      // 加载考试进度（从数据库）- 仅在未提交过时恢复
      if (!hasSubmitted) {
        const { data: progressData } = await supabase.from('exam_progress').select('*').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
        if (progressData) {
          const savedAt = new Date(progressData.saved_at);
          const now = new Date();
          const hoursDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
          if (progressData.is_submitted && hoursDiff <= 48) {
            setIsSubmitted(true);
            setScore(progressData.score || 0);
            if (progressData.answers) setAnswers(progressData.answers as Record<string, string>);
            if (progressData.current_index) setCurrentIndex(progressData.current_index);
            if (progressData.ai_explanations) setAiExplanations(progressData.ai_explanations as Record<string, string>);
          } else if (!progressData.is_submitted) {
            // 未提交的进度：恢复答题状态
            if (progressData.answers) setAnswers(progressData.answers as Record<string, string>);
            if (progressData.current_index) setCurrentIndex(progressData.current_index);
            if (progressData.ai_explanations) setAiExplanations(progressData.ai_explanations as Record<string, string>);
          }
          if (progressData.marked_questions) setMarkedQuestions(new Set(progressData.marked_questions));
          if (progressData.time_left) setTimeLeft(progressData.time_left);
        }
      }

      // 加载AI阅卷开关和答题模式设置
      const { data: examSettings } = await supabase.from('exam_settings').select('ai_grading_enabled, answer_mode').single();
      if (examSettings) {
        setAiGradingEnabled(examSettings.ai_grading_enabled ?? false);
        const paperAnswerMode = paper?.answer_mode as 'upload' | 'online' | undefined;
        setAnswerMode(paperAnswerMode || (examSettings.answer_mode as 'upload' | 'online') || 'online');
      }

      // 检查是否是作业模式（URL中有assignmentId参数）
      const searchParams = new URLSearchParams(location.search);
      const assignmentId = searchParams.get('assignmentId');
      setIsAssignmentMode(!!assignmentId);

      // 如果没有提交记录（重新开始答题），重置自动保存状态
      if (!hasSubmitted) {
        resetAutoSave();
      }
    };

    loadData();
  }, [currentUser?.id, examKey, isPreview, resetAutoSave]);

  // ============ 页面刷新恢复逻辑 ============
  // 如果数据库中没有进度数据，尝试从localStorage恢复
  useEffect(() => {
    if (!currentUser?.id || isPreview || restoredFromCache) return;

    // 仅在数据库进度未加载时尝试localStorage恢复
    // 这个逻辑在 loadData 完成后执行，如果 answers 仍为空则尝试恢复
    const tryRestoreFromCache = async () => {
      // 先检查数据库是否已有进度
      const { data: progressData } = await supabase.from('exam_progress').select('id').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
      if (progressData) return; // 数据库有数据，不需要从缓存恢复

      // 数据库无数据，尝试从localStorage恢复
      const cachedData = getLocalCacheData();
      if (cachedData && !cachedData.isSubmitted) {
        setAnswers(cachedData.answers);
        setCurrentIndex(cachedData.currentIndex);
        setTimeLeft(cachedData.timeLeft);
        setMarkedQuestions(new Set(cachedData.markedQuestions));
        if (cachedData.aiExplanations) setAiExplanations(cachedData.aiExplanations);
        setRestoredFromCache(true);
      }
    };

    tryRestoreFromCache();
  }, [currentUser?.id, examKey, isPreview, restoredFromCache]);

  // ============ 倒计时 ============

  useEffect(() => {
    if (isSubmitted || isPreview) return;
    if (timeLeft <= 0) {
      handleAutoSubmit();
      return;
    }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, isSubmitted, isPreview]);

  // ============ 提交相关逻辑 ============

  const handleAutoSubmit = async () => {
    await confirmSubmit();
    alert('试卷已自动提交成功');
  };

  const calculateScore = () => {
    let totalScore = 0;
    examQuestions.forEach(q => {
      const answer = answers[q.id];
      if (!answer) return;
      if (q.type === 'multi_choice') {
        const selectedIds = answer.split(',');
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
          totalScore += q.score;
        }
      } else if (q.type === 'single_choice' || q.type === 'judge') {
        const selectedOption = q.options?.find(o => o.id === answer);
        if (selectedOption?.isCorrect) totalScore += q.score;
      }
    });
    return totalScore;
  };

  const confirmSubmit = async () => {
    if (isPreview) {
      alert('预览模式不能提交答案');
      return;
    }

    const finalScore = calculateScore();
    setScore(finalScore);
    setIsSubmitted(true);

    // 提交前先保存一次进度
    await saveNow();

    // 保存答题记录
    let examRecordId = null;
    if (currentUser?.id) {
      try {
        const { count } = await supabase.from('exam_records').select('*', { count: 'exact' }).eq('user_id', currentUser.id).eq('paper_id', paperId || 'custom');
        const submitCount = (count || 0) + 1;
        const totalScore = paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0);
        const isPassed = finalScore >= totalScore * 0.6;
        const timeUsed = EXAM_DURATION - timeLeft;

        const hasSubjectiveQuestions = examQuestions.some(q =>
          q.type === 'coding' || q.type === 'scratch_coding' || q.type === 'python_coding' || q.type === 'cpp_coding'
        );
        const isObjective = !hasSubjectiveQuestions;
        const gradingStatus = isObjective ? 'final' : 'pending';

        const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
        const objectiveScore = examQuestions.reduce((total, q) => {
          if (!objectiveTypes.includes(q.type)) return total;
          const userAnswer = answers[q.id];
          if (!userAnswer) return total;
          if (q.type === 'multi_choice') {
            const selectedIds = userAnswer.split(',');
            const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
            if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
              return total + (q.score || 0);
            }
          } else {
            const selectedOption = q.options?.find(o => o.id === userAnswer);
            if (selectedOption?.isCorrect) return total + (q.score || 0);
          }
          return total;
        }, 0);

        const objectiveTotalScore = examQuestions.reduce((total, q) => {
          if (objectiveTypes.includes(q.type)) {
            return total + (q.score || 0);
          }
          return total;
        }, 0);

        const programmingScore = null;

        const { data: examRecord } = await supabase.from('exam_records').insert({
          user_id: currentUser.id,
          paper_id: paperId || 'custom',
          paper_title: paper?.title || '自定义组卷',
          score: finalScore,
          total_score: totalScore,
          time_left: timeUsed,
          exam_duration: EXAM_DURATION,
          submit_count: submitCount,
          is_passed: isPassed,
          answers: answers as any,
          questions: examQuestions as any,
          is_objective: isObjective,
          grading_status: gradingStatus,
          objective_score: objectiveScore,
          objective_total_score: objectiveTotalScore,
          programming_score: programmingScore
        }).select().single();

        examRecordId = examRecord?.id;

        // 提交成功后删除exam_progress记录，确保重新进入时从初始状态开始
        await supabase.from('exam_progress').delete().eq('user_id', currentUser.id).eq('exam_id', examKey);

        // 提交成功后清除本地缓存
        clearLocalCache();

        // 检查是否是从作业入口进入的答题
        const searchParams = new URLSearchParams(location.search);
        const assignmentId = searchParams.get('assignmentId');

        if (assignmentId && examRecordId) {
          const session = (await supabase.auth.getSession()).data.session;
          if (session) {
            await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                action: 'complete_assignment',
                assignmentId,
                examRecordId,
                score: finalScore,
              }),
            });
          }
        }
      } catch (error) {
        console.error('保存答题记录失败:', error);
      }
    }

    // 自动记录错题
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session?.access_token && currentUser?.id && paperId) {
        const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
        const wrongQuestions: any[] = [];

        for (const q of examQuestions) {
          if (!objectiveTypes.includes(q.type)) continue;
          const userAnswer = answers[q.id];
          if (!userAnswer) continue;

          let isWrong = false;
          if (q.type === 'multi_choice') {
            const selectedIds = userAnswer.split(',');
            const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
            isWrong = !(selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id)));
          } else {
            const selectedOption = q.options?.find(o => o.id === userAnswer);
            isWrong = !selectedOption?.isCorrect;
          }

          if (isWrong) {
            wrongQuestions.push({
              question_id: q.id,
              paper_id: paperId,
              organization_code: paper?.organization || '',
              category_code: paper?.category_type || '',
              level_text: paper?.level_text || '',
              question_type: q.type,
              user_answer: userAnswer  // 保存学员的答案
            });
          }
        }

        if (wrongQuestions.length > 0) {
          await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              action: 'add_wrong_questions',
              questions: wrongQuestions
            })
          });
        }
      }
    } catch (error) {
      // 静默处理
    }

    // 调用U币服务计算经验
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session?.access_token && currentUser?.id && paperId) {
        const totalScore = paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0);
        const answerTimeSeconds = EXAM_DURATION - timeLeft;

        const { count } = await supabase
          .from('exam_records')
          .select('*', { count: 'exact' })
          .eq('user_id', currentUser.id)
          .eq('paper_id', paperId)
          .lt('created_at', new Date().toISOString());

        const isFirstSubmit = !count || count === 0;
      }
    } catch (error) {
      console.error('计算U币失败:', error);
    }

    // 跳转到测评结果页
    const typeStats = examQuestions.reduce((acc, q) => {
      const typeKey = q.type;
      if (!acc[typeKey]) acc[typeKey] = { total: 0, correct: 0, score: 0, totalScore: 0 };
      acc[typeKey].total++;
      acc[typeKey].totalScore += q.score;
      const userAnswer = answers[q.id];
      if (userAnswer) {
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        let isCorrect = false;
        if (q.type === 'multi_choice') {
          const userIds = userAnswer.split(',');
          isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
        } else {
          isCorrect = correctIds.includes(userAnswer);
        }
        if (isCorrect) {
          acc[typeKey].correct++;
          acc[typeKey].score += q.score;
        }
      }
      return acc;
    }, {} as Record<string, { total: number; correct: number; score: number; totalScore: number }>);

    navigate(`/exam-result/${paperId || 'custom'}`, {
      state: {
        paperId: paperId || 'custom',
        paperTitle: paper?.title || '自定义组卷',
        score: finalScore,
        totalScore: paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0),
        timeLeft,
        examDuration: EXAM_DURATION,
        answers,
        questions: examQuestions,
        typeStats
      }
    });
  };

  const handleSubmit = () => {
    const unansweredCount = examQuestions.length - answeredCount;
    if (unansweredCount > 0) {
      if (!confirm(`您还有 ${unansweredCount} 道题未作答，确定要提交吗？`)) return;
    } else if (!confirm('确定要提交试卷吗？提交后将无法修改答案。')) return;
    confirmSubmit();
  };

  const handleRestart = async () => {
    if (!currentUser?.id) return;
    setIsSubmitted(false);
    setScore(0);
    setAnswers({});
    setMarkedQuestions(new Set());
    setCurrentIndex(0);
    setTimeLeft(EXAM_DURATION);
    setAiExplanations({});
    clearLocalCache();
    await supabase.from('exam_progress').delete().eq('user_id', currentUser.id).eq('exam_id', examKey);
  };

  const toggleMark = () => {
    const newMarked = new Set(markedQuestions);
    if (newMarked.has(currentIndex)) newMarked.delete(currentIndex);
    else newMarked.add(currentIndex);
    setMarkedQuestions(newMarked);
  };

  const getAIGrading = async (question: Question) => {
    const code = answers[question.id] || '';
    const textAnswer = answers[`${question.id}_text`] || '';
    if (!code && !textAnswer) return;
    try {
      const response = await fetch('/api/ai-grade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, answer: code || textAnswer })
      });
      const result = await response.json();
      if (result.success) {
        setAiExplanations({ ...aiExplanations, [question.id]: result.feedback });
        setAnswers({ ...answers, [`${question.id}_aiScore`]: result.score });
      }
    } catch (error) {
      
    }
  };

  // ============ 答题变更处理（含防抖保存触发） ============

  const handleAnswerChange = (questionId: string, answer: string) => {
    if (answer) {
      setAnswers({ ...answers, [questionId]: answer });
    } else {
      const newAnswers = { ...answers };
      delete newAnswers[questionId];
      setAnswers(newAnswers);
    }
    // 触发防抖自动保存
    triggerDebouncedSave();
  };

  const handleTextAnswerChange = (questionId: string, text: string) => {
    if (text.trim()) {
      setAnswers({ ...answers, [questionId]: text, [`${questionId}_text`]: text });
    } else {
      const newAnswers = { ...answers };
      delete newAnswers[questionId];
      delete newAnswers[`${questionId}_text`];
      setAnswers(newAnswers);
    }
    // 触发防抖自动保存
    triggerDebouncedSave();
  };

  // ============ 工具函数 ============

  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const getQuestionTypeName = (type: string, subType?: string) => {
    if (type === 'scratch_coding') return categoryStore.getCategoryNameByCode('scratch') || '图形化编程';
    if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
    return typeNames[type] || '单选题';
  };

  // 预览模式：保存缩放设置到数据库
  const handleSaveWidthSettings = async () => {
    if (isSavingWidths) return;
    setIsSavingWidths(true);
    try {
      for (const q of examQuestions) {
        await supabase.from('questions').update({
          title_image_widths: q.titleImageWidths || [],
          title_image_layout: (q as any).titleImageLayout || 'auto',
          image_position: (q as any).imagePosition || 'below',
          option_image_widths: q.options?.reduce((acc: Record<string, number[]>, o: any) => {
            const optionImageWidths = o.imageWidths && o.imageWidths.length > 0
              ? o.imageWidths
              : (o.imageWidth ? [o.imageWidth] : []);
            if (optionImageWidths.length > 0) acc[o.id] = optionImageWidths;
            return acc;
          }, {}) || {},
          options: q.options || [],
          option_layout: (q as any).optionLayout || 'grid'
        }).eq('id', q.id);
      }
      showToast('保存成功', 'success');
      const snapshot: Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidths?: number[] }[] }> = {};
      examQuestions.forEach((q: Question) => {
        snapshot[q.id] = {
          titleImageWidths: q.titleImageWidths ? [...q.titleImageWidths] : undefined,
          optionImageWidths: q.options?.map(o => {
            const optionImageWidths = o.imageWidths && o.imageWidths.length > 0
              ? o.imageWidths
              : (o.imageWidth ? [o.imageWidth] : []);
            return { id: o.id, imageWidths: optionImageWidths };
          })
        };
      });
      setInitialWidths(snapshot);
      setHasWidthChanges(false);
    } catch (error) {
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSavingWidths(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  // 获取本地缓存数据的辅助函数
  const getLocalCacheData = (): AutoSaveData | null => {
    try {
      const raw = localStorage.getItem(`autosave_${examKey}`);
      if (!raw) return null;
      const data = JSON.parse(raw) as AutoSaveData;
      const savedAt = new Date(data.savedAt);
      const hoursDiff = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
      if (hoursDiff > 24) {
        localStorage.removeItem(`autosave_${examKey}`);
        return null;
      }
      return data;
    } catch {
      localStorage.removeItem(`autosave_${examKey}`);
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs lg:text-sm">返回</span>
            </button>
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-xs lg:text-sm hidden sm:inline">首页</span>
            </button>
          </div>
          <div className="flex flex-col items-center min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
              {paper?.title?.split('图形化')[0] || '在线考试'}
            </h1>
            {paper?.title?.includes('图形化') && <p className="text-xs lg:text-xl text-gray-500 truncate">图形化{paper.title.split('图形化')[1]}</p>}
          </div>
          <div className="flex items-center gap-2 lg:gap-4 shrink-0">
            {isSubmitted && (
              <div className="flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1 lg:py-2 rounded-lg border bg-red-50 border-red-200">
                <span className="text-red-600 font-bold text-sm lg:text-xl">得分：{score}分</span>
              </div>
            )}
            {paperId && isEditorPreview && (
              <button
                onClick={() => {
                  if (isEditorPreview) {
                    onBack();
                  } else {
                    navigate(`/admin/paper-editor?edit=${paperId}`);
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>试卷编辑</span>
              </button>
            )}
            {/* 自动保存状态指示器 - 内联到Header中倒计时左侧 */}
            {!isPreview && (
              <AutoSaveStatusIndicatorInline statusInfo={statusInfo} />
            )}
            <div className={`flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1 lg:py-2 rounded-lg border ${
              isPreview
                ? 'bg-gray-100 border-gray-300 text-gray-400'
                : isTimeWarning
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-white border-blue-200 text-blue-700'
            }`}>
              <Clock className={`w-3 h-3 lg:w-4 lg:h-4 ${isTimeWarning && !isPreview ? 'animate-spin' : ''}`} />
              <span className="font-mono font-bold text-xs lg:text-base">
                {isSubmitted ? (() => {
                  const usedTime = EXAM_DURATION - timeLeft;
                  const h = Math.floor(usedTime / 3600);
                  const m = Math.floor((usedTime % 3600) / 60);
                  const s = usedTime % 60;
                  if (h > 0) return `用时：${h}小时${m}分${s}秒`;
                  return `用时：${m}分${s}秒`;
                })() : formatTime(timeLeft)}
              </span>
            </div>
            {!isSubmitted ? (
              <>
                {isEditorPreview ? (
                  <button
                    onClick={handleSaveWidthSettings}
                    disabled={!hasWidthChanges || isSavingWidths}
                    className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                      isSavingWidths
                        ? 'bg-emerald-400 text-white cursor-wait'
                        : hasWidthChanges
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isSavingWidths ? '保存中...' : '保存试卷'}
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={isPreview}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      isPreview ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >
                    提交试卷
                  </button>
                )}
              </>
            ) : (
              <button onClick={() => { if (confirm('确定要重新测试吗？当前答题记录将被清空。')) handleRestart(); }} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                重新测试
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        {/* Mobile Answer Card Button */}
        <button onClick={() => setShowSidebar(true)} className="lg:hidden fixed bottom-4 right-4 z-40 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Sidebar - Answer Card */}
          <div className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
            <AnswerCard
              examQuestions={examQuestions}
              currentIndex={currentIndex}
              answers={answers}
              markedQuestions={markedQuestions}
              isSubmitted={isSubmitted}
              paper={paper}
              onQuestionSelect={setCurrentIndex}
            />
          </div>

          {/* Question Area */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Question Header */}
              <div className="sticky lg:top-24 z-30 bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 border-b border-blue-200">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                    <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
                      {getQuestionTypeName(currentQuestion?.type, currentQuestion?.subType)}
                    </span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2">
                    <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                    </button>
                    <button onClick={() => setCurrentIndex(Math.min(examQuestions.length - 1, currentIndex + 1))} disabled={currentIndex === examQuestions.length - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 lg:gap-2 ml-auto">
                    {!isSubmitted && !isPreview && (
                      <button
                        onClick={toggleMark}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 text-sm ${
                          markedQuestions.has(currentIndex)
                            ? 'bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200'
                            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {markedQuestions.has(currentIndex) ? <><BookmarkCheck className="w-4 h-4" />已标记</> : <><Bookmark className="w-4 h-4" />标记</>}
                      </button>
                    )}
                    {isPreview && (
                      <PreviewControls
                        question={currentQuestion}
                        examQuestions={examQuestions}
                        onExamQuestionsChange={setExamQuestions}
                        onHasWidthChangesChange={setHasWidthChanges}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
                <div className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold flex-shrink-0">{currentIndex + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-3 relative">
                        {(() => {
                          const imagePosition = (currentQuestion as any).imagePosition || 'below';
                          const currentLayout = (currentQuestion as any)?.titleImageLayout || 'auto';
                          const hasTitleImages = currentQuestion?.titleImages && currentQuestion.titleImages.length > 0 && !currentQuestion?.questionStem?.includes('<img');
                          const hasTitleContentImages = currentQuestion?.questionStem?.includes('<img');

                          const isCodingQuestion = currentQuestion?.type === 'python_coding' ||
                                                   currentQuestion?.type === 'cpp_coding' ||
                                                   currentQuestion?.type === 'scratch_coding';

                          const questionTitle = isCodingQuestion
                            ? (currentQuestion?.questionTitle ?? (() => {
                                const codingQuestions = examQuestions.filter(q =>
                                  q.type === 'python_coding' || q.type === 'cpp_coding' || q.type === 'scratch_coding'
                                );
                                const codingIndex = codingQuestions.findIndex(q => q.id === currentQuestion?.id) + 1;
                                return `未命名${codingIndex}`;
                              })())
                            : null;

    const questionStem = currentQuestion?.questionStem || currentQuestion?.content || '';
                          const titleText = questionStem?.includes('<img') || questionStem?.startsWith('<') ? (
                            <QuestionHtmlWithZoom
                              html={questionStem}
                              imageWidths={currentQuestion.titleImageWidths}
                            />
                          ) : (
                            <span>{questionStem}</span>
                          );

                          const gridCols = getTitleImageGridCols(currentLayout, currentQuestion.titleImages?.length || 1);

                          const titleImagesEl = hasTitleImages ? (
                            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
                              {currentQuestion.titleImages?.map((imgUrl, idx) => {
                                const fullUrl = getImageUrl(imgUrl);
                                const savedWidth = currentQuestion.titleImageWidths?.[idx];
                                const displayWidth = savedWidth != null ? savedWidth : 100;
                                return (
                                  <div key={idx} className="relative overflow-hidden">
                                    <img
                                      src={fullUrl}
                                      alt={`题目图片 ${idx + 1}`}
                                      className="question-img rounded-lg border border-gray-200"
                                      style={{ width: `${displayWidth}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                      loading="lazy"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    {isPreview && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="text-xs text-gray-500">缩放:</span>
                                        <input
                                          type="range"
                                          min="10"
                                          max="100"
                                          step="1"
                                          value={displayWidth}
                                          onChange={(e) => {
                                            const val = parseInt(e.target.value);
                                            const newWidths = [...(currentQuestion.titleImageWidths || currentQuestion.titleImages?.map(() => 50) || [])];
                                            newWidths[idx] = val;
                                            setHasWidthChanges(true);
                                            setExamQuestions(examQuestions.map(q =>
                                              q.id === currentQuestion.id ? { ...q, titleImageWidths: newWidths } : q
                                            ));
                                          }}
                                          className="w-20 h-2 accent-blue-500 cursor-pointer"
                                        />
                                        <input
                                          type="number"
                                          min="10"
                                          max="100"
                                          value={displayWidth}
                                          onChange={(e) => {
                                            const val = parseInt(e.target.value);
                                            if (!isNaN(val) && val >= 10 && val <= 100) {
                                              const newWidths = [...(currentQuestion.titleImageWidths || currentQuestion.titleImages?.map(() => 50) || [])];
                                              newWidths[idx] = val;
                                              setHasWidthChanges(true);
                                              setExamQuestions(examQuestions.map(q =>
                                                q.id === currentQuestion.id ? { ...q, titleImageWidths: newWidths } : q
                                              ));
                                            }
                                          }}
                                          className="w-16 h-5 text-sm text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                                        />
                                        <span className="text-xs text-gray-400">%</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : null;

                          return (
                            <>
                              {isCodingQuestion && (
                                <div className="mb-3 pb-3 border-b border-gray-200">
                                  <span className="text-sm font-medium text-gray-500">题目名称：</span>
                                  <span className="text-base font-semibold text-gray-800">{questionTitle}</span>
                                </div>
                              )}
                              {imagePosition === 'above' && titleImagesEl}
                              {titleText}
                              {imagePosition === 'below' && titleImagesEl}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Answer Area */}
              <div className="p-3 sm:p-4 lg:p-6">
                <QuestionRenderer
                  key={currentQuestion?.id}
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  answers={answers}
                  isSubmitted={isSubmitted}
                  isPreview={isPreview}
                  paperId={paperId}
                  paper={paper}
                  currentUser={currentUser}
                  aiExplanations={aiExplanations}
                  examQuestions={examQuestions}
                  aiGradingEnabled={aiGradingEnabled}
                  isAssignmentMode={isAssignmentMode}
                  answerMode={answerMode}
                  onAnswerChange={handleAnswerChange}
                  onTextAnswerChange={handleTextAnswerChange}
                  onAIGrading={getAIGrading}
                  onExamQuestionsChange={setExamQuestions}
                  onHasWidthChangesChange={setHasWidthChanges}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.3 }} className="fixed inset-y-0 right-0 w-[85vw] sm:w-80 bg-white shadow-xl z-50 lg:hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">答题卡</h2>
                <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-600" /></button>
              </div>
              <div className="p-4 overflow-y-auto">
                <AnswerCard
                  examQuestions={examQuestions}
                  currentIndex={currentIndex}
                  answers={answers}
                  markedQuestions={markedQuestions}
                  isSubmitted={isSubmitted}
                  paper={paper}
                  onQuestionSelect={setCurrentIndex}
                  onCloseSidebar={() => setShowSidebar(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
